// Simple auth context + fetch helper for the admin back-office.
// Token stored in localStorage; attached as Authorization: Bearer to axios requests.
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "phenix_admin_token";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    try {
      return window.localStorage.getItem(TOKEN_KEY) || null;
    } catch {
      return null;
    }
  });
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const persistToken = useCallback((t) => {
    setToken(t);
    try {
      if (t) window.localStorage.setItem(TOKEN_KEY, t);
      else window.localStorage.removeItem(TOKEN_KEY);
    } catch {
      // ignore
    }
  }, []);

  // Fetch /me whenever token changes
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setUser(null);
      setChecking(false);
      return;
    }
    setChecking(true);
    axios
      .get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!cancelled) setUser(res.data);
      })
      .catch(() => {
        if (!cancelled) {
          persistToken(null);
          setUser(null);
        }
      })
      .finally(() => !cancelled && setChecking(false));
    return () => {
      cancelled = true;
    };
  }, [token, persistToken]);

  const login = useCallback(
    async (email, password) => {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      persistToken(res.data.access_token);
      setUser(res.data.user);
      return res.data.user;
    },
    [persistToken]
  );

  const logout = useCallback(() => {
    persistToken(null);
    setUser(null);
  }, [persistToken]);

  const authFetch = useCallback(
    (method, path, data) =>
      axios.request({
        method,
        url: `${API}${path}`,
        data,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }),
    [token]
  );

  return (
    <AdminAuthContext.Provider
      value={{ token, user, checking, login, logout, authFetch }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
